/**
 * Responsible for creating a slider zoom or in other words a carousel where the
 * images in the carousel can be interacted with so that they can be zoomed in
 * @module modules/zoom/sliderZoom
 */
import $ from 'jquery';
import '../slick-carousel';
import $script from 'scriptjs';

import customPaging from '../slick/custom-paging';
import createCounter from '../slick/counter';
import { get } from '../../lib/props';
import imageZoom from './image-zoom';
import Keyboard from '../keyboard';

const HAMMERJS_FALLBACK = 'https://ajax.googleapis.com/ajax/libs/hammerjs/2.0.8/hammer.min.js';

/**
 *
 * AB Test 899 HP/PDP Increased Slick Sensitivity
 */
const increasedSensitivity = get('slick.slickSensitivity', false);



/**
 * Makes a request for HammerJS.
 * @private
 * @return {Promise} Denotes when the request has been successfully executed
 */
const loadHammerJSFunc = () =>
  new Promise((resolve) => {
    $script(get('scripts.hammerJS', HAMMERJS_FALLBACK), () => {
      resolve(window.Hammer);
    });
  });

/**
 * Defines the options which can be provided for the image zoom.
 * @type {Object}
 */
const DEFAULTS = {
  loadHammerJS: loadHammerJSFunc,
  startingIndex: 0,
  speed: 300,
  slideFunc: num => num,
  menuRender: () => '',
  didRender: () => {},
  didUnmount: () => {},
  didSlideTransition: (prev, current) => ({ prev, current }),
  zoomClickListener: () => { },
  noImages: '',
  slickContainer: '',
  counterContainer: '',
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
  releaseFocusFunc: Keyboard.releaseFocus.bind(Keyboard),
};

/**
 * Defines the id for the close element.
 * @type {String}
 */
export const CLOSE_ID = 'close-zoom-menu';

/**
 * Defines the id for the zoom slider.
 * @type {String}
 */
export const ZOOM_SLIDER_ID = 'zoom-slider';

/**
 * Defines the content className
 * @type {String}
 */
export const CONTENT_CLASSNAME = 'zoom-slider__content';

/**
 * Binds an event to the close element
 * @private
 * @param  {Function} callback - The event handler for the close element
 */
const bindClose = (callback) => {
  const $closeButton = $(document.getElementById(CLOSE_ID));

  if ($closeButton.length) {
    $closeButton.on('click.closeZoom', callback);
  } else {
    console.warn(`Element with ID "${CLOSE_ID}" not found.`);
  }

  // Add this back:
  $(document).on('keydown.closeZoom', (e) => {
    if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
      if (!$('.modal').is(':visible')) return;
      e.preventDefault();
      callback();
    }
  });
};

/**
 * Closes the dialog.
 * @private
 * @param  {Dialog} dialog - Instance of a dialog
 * @param  {Function} didUnmount - Called once the dialog has closed
 */
const closeDialog = (didUnmount) => {
  // Unbind the click and keydown events on close
  $('.image-carousel').off('click.product-zoom');
  $('.image-carousel').off('keydown.product-zoom');

  // Focus back on the parent element
  $('.image-carousel').focus();

  didUnmount();
};

/**
 * Stupid helper that concats the element id based on an id for a slide.
 * @private
 * @param  {string} id - The id of the slide being requested
 * @return {string} - A slide id
 */
const getSlideId = id => `zoom-slider__item-${id}`;

/**
 * Obtains a slide or image basd on the id provided in the template.
 * @private
 * @param  {number} id - The id of the slide being requested
 * @return {HTMLElement} An element representing a slide or image
 */
const getSlide = id => document.getElementById(getSlideId(id));

/**
 * Executes in the future the close callback. The callback is responsible for
 * clean up to ensure nothing is left in memory.
 * @private
 * @param  {jQuery} $zoomSlider
 * @param  {Hammer} hammer - An instance of Hammer from hammerjs
 * @param  {Function} didUnmount - Used to denote when closed
 * @return {Function} Callback function used for the event
 */
 const teardownFull = ($zoomSlider, hammer, beforeUnmount,
   didUnmount, zoomClickListener) => () => {
     beforeUnmount();
     hammer.destroy();
     if ($zoomSlider.hasClass('slick-initialized')) $zoomSlider.slick('unslick');
     // Remove the modal HTML from the DOM
     $('#zoom-modal').remove();
     closeDialog(didUnmount);
     zoomClickListener();

     $(document).off('keydown.closeZoom');
   };

/**
 * Simple rendering when no images are found.
 * @private
 * @param  {String} noImages - Text indicating no images were found
 * @return {string} Template containing content around no visible images
 */
const renderNoImages = ({ noImages }) => `
  <p class="${CONTENT_CLASSNAME}">${noImages}</p>
`;

/**
 * Template containing what is to be rendered in the dialog.
 * @private
 * @param  {array} images - A list of images
 * @param  {Function} [menuRender=() => ''] - Used for additional rendering
 * @return {String} A fully rendered template as a string
 */
const renderDialog = ({ images, noImages, menuRender = () => '' }) => `
    <div id="zoom-modal" class="modal modal--zoom">
      <h2 class="modal__hed u-screen-reader">Zoom Modal</h2>
      <div id="zoom-menu" class="zoom-menu">
        <button id="${CLOSE_ID}" class="js-modal-close zoom-menu__close">
          <span class="icon icon--lg icon--x">
            <svg viewBox="0 0 16 16"><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-x"></use></svg>
          </span>
        </button>
      </div>
      <ul id="${ZOOM_SLIDER_ID}" class="zoom-slider u-margin-l--none">
        ${images.length
          ?
          images.map((image, i) =>
            `<li><img src="${image}" class="zoom-slider__item" id="${getSlideId(i)}"></li>`,
          ).join('')
          :
          renderNoImages({ noImages })
        }
      </ul>
    </div>
  `;

/**
 * Focuses on the modal.
 * @private
 * @param  {jQuery} $dialog - jQuery object to focus on
 */
const focusModal = ($dialog) => {
  const $slickIndex = $dialog.find('.slick-active');
  $slickIndex.attr('tabindex', 0);
  $slickIndex.focus();
};

/**
 * Merges or concats options to be passed along to slick.
 * @private
 * @param  {Object} [options={}] - Options sent along to slick
 * @return {Object} A mergingin of options for slick
 */
const mergeSliderOptions = (options = {}) => {
  // Mock Content Start - AB Test 899 HP/PDP Increased Slick Sensitivity
  if (increasedSensitivity) document.body.classList.add('ab899-slick-sensitivity');
  // Mock Content End
  return $.extend({
    arrows: false,
    dots: false,
    infinite: true,
    slidesToShow: 1,
    appendDots: '#zoom-menu',
    dotsClass: 'slider-dots__menu image-carousel__dots',
    slide: 'li',
    customPaging,
    // Mock Content Start - AB Test 899 HP/PDP Increased Slick Sensitivity
    touchThreshold: increasedSensitivity ? 20 : 5,
    vertical: false,
    verticalSwiping: false,
    // Mock Content End
  }, options);
};

/**
 * Responsible for rendering a slider zoom. More specifically, it will create
 * a dialog/modal such that n number of images are used for creating a slider.
 * On top of that, each image will have the ability for pinch/zoom/moving
 * around an image such that the image can be examined by the end user.
 * @see DEFAULTS
 * @param {array} images - A list of images used for the slider and zooming
 * @param {Object} options - Additional hooks to interact with the slider zoom
 * @return {Promise} Denotes when the slier zoom has been fully rendered
 */
export default function sliderZoom(images, options = {}) {
  const {
    startingIndex,
    speed,
    slideFunc,
    menuRender,
    didRender,
    beforeUnmount,
    didUnmount,
    loadHammerJS,
    didSlideTransition,
    noImages,
    zoomClickListener,
    slickContainer,
    counterContainer,
    trapFocusFunc,
  } = $.extend({}, DEFAULTS, options);

  return loadHammerJS().then((Hammer) => {
    // Remove any existing modal before creating new one
    $('#zoom-modal').remove();

    const output = renderDialog({ images, menuRender, noImages });
    const $modalHtml = $(output);
    $modalHtml.appendTo('body');

    $.modal({
      type: 'full',
      open: 'zoom-modal',
      triggerOpen: true,
    });

    didRender();

    if (!images.length) {
      bindClose(() => {
        $('#zoom-modal').remove();
        closeDialog(didUnmount);
      });
      return;
    }

    slideFunc(startingIndex);

    const $zoomSlider = $(document.getElementById(ZOOM_SLIDER_ID));
    let currentSliderItem = getSlide(startingIndex);
    let hammerInstance = imageZoom(Hammer, currentSliderItem);
    let currentNum = startingIndex;
    let previous;

    createCounter($zoomSlider, slickContainer, counterContainer);

    $zoomSlider
      .slick(mergeSliderOptions({
        speed,
        initialSlide: startingIndex,
      }))

      .on('afterChange', (evt, slide, currentSlide) => {
        // when at the beginning or end, do not reset zooming
        if (currentNum === currentSlide) {
          return;
        }

        currentNum = currentSlide;
        const $currentSlide = $(slide.$slides.get(currentSlide));
        // add tabindex to the current slide
        // using setTimeout because slick adds tabindex -1
        // so its race condition with focus
        setTimeout(() => {
        $currentSlide.prop('tabindex', 0);
        $currentSlide.focus();
        }, 100);
        slideFunc(currentNum);
        previous = currentSliderItem;
        hammerInstance.destroy();

        currentSliderItem = getSlide(currentNum);
        hammerInstance = imageZoom(Hammer, currentSliderItem);

        // use a setTimeout instead of the afterChange event since it'll
        // provide to long of a delay between swipe change when the image
        // has been zoomed in too much, yeah, a whole lot of edge cases
        // happening here.
        setTimeout(() => {
          /* eslint-disable no-param-reassign */
          previous.style.transform = 'none';
          /* eslint-enable no-param-reassign */
          didSlideTransition(previous, currentSliderItem);
        }, speed);
      });
    focusModal($zoomSlider);
    bindClose(teardownFull($zoomSlider, hammerInstance, beforeUnmount,
      didUnmount, zoomClickListener));
  });
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/zoom/slider-zoom.js